import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { app, auth } from "./firebaseConfig";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Visibility, VisibilityOff } from "@mui/icons-material";

/**
 * Invite-acceptance landing page (signup-flow-modernization branch).
 *
 * Flow:
 *   1. User clicks /join/:token from the invite email.
 *   2. On mount, calls getInviteDetails({token}) to fetch org name + email.
 *   3. User sees an EILDocs-style invite card with the org name + a locked
 *      email field, then fills name + password + confirm.
 *   4. On submit, calls createNewUser with signUpOption=JOIN_ORGANIZATION
 *      — the existing JOIN_ORGANIZATION branch creates the user with
 *      status="active" (the admin approval gate was dropped in Phase 4),
 *      sends an informational inviteAwaitingApproval notification to the
 *      admin, and returns a custom token for auto-sign-in.
 *   5. Auto-sign-in via signInWithCustomToken, then navigate to
 *      /app/dashboard.
 */
function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [phase, setPhase] = useState("loading"); // loading | invalid | form | submitting | success
  const [errorMessage, setErrorMessage] = useState("");
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const loadDetails = async () => {
      if (!token) {
        setPhase("invalid");
        setErrorMessage(t("accept.errorMissingToken"));
        return;
      }
      try {
        const functions = getFunctions(
          app,
          process.env.REACT_APP_FUNCTIONS_REGION,
        );
        const fn = httpsCallable(functions, "getInviteDetails");
        const result = await fn({ token });
        const data = result.data || {};
        if (data.success && data.orgName && data.email) {
          setOrgName(data.orgName);
          setEmail(data.email);
          setPhase("form");
        } else {
          setPhase("invalid");
          setErrorMessage(data.reason || t("accept.errorInvalid"));
        }
      } catch (err) {
        console.error("[AcceptInvite] getInviteDetails failed:", err);
        const message = err?.message || t("accept.errorLoadFailed");
        setErrorMessage(
          message.includes("expired")
            ? t("accept.errorExpired")
            : t("accept.errorInvalid"),
        );
        setPhase("invalid");
      }
    };
    loadDetails();
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!displayName.trim()) {
      setSubmitError(t("accept.validationNameRequired"));
      return;
    }
    if (password.length < 6) {
      setSubmitError(t("accept.validationPasswordTooShort"));
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError(t("accept.validationPasswordMismatch"));
      return;
    }

    setPhase("submitting");
    try {
      const functions = getFunctions(
        app,
        process.env.REACT_APP_FUNCTIONS_REGION,
      );
      const createNewUser = httpsCallable(functions, "createNewUser");
      const result = await createNewUser({
        signUpOption: "JOIN_ORGANIZATION",
        inviteToken: token,
        displayName: displayName.trim(),
        email,
        password,
      });
      const data = result.data || {};

      if (data.customToken) {
        try {
          await signInWithCustomToken(auth, data.customToken);
        } catch (signInErr) {
          console.warn(
            "[AcceptInvite] signInWithCustomToken failed:",
            signInErr,
          );
        }
      }
      setPhase("success");
      // No admin approval gate anymore — invitee is created with
      // status=active, can use the app immediately.
      setTimeout(() => navigate("/app/dashboard"), 1500);
    } catch (err) {
      console.error("[AcceptInvite] createNewUser failed:", err);
      const message = err?.message || t("accept.errorSubmitFallback");
      setSubmitError(message);
      setPhase("form");
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {phase === "loading" && (
          <>
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              {t("accept.loadingTitle")}
            </Typography>
          </>
        )}

        {phase === "invalid" && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main" }} />
            <Typography variant="h5" component="h1" sx={{ mt: 2 }}>
              {t("accept.invalidTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              {errorMessage}
            </Typography>
            <Button variant="contained" onClick={() => navigate("/")}>
              {t("accept.returnHome")}
            </Button>
          </>
        )}

        {(phase === "form" || phase === "submitting") && (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                bgcolor: "action.hover",
                p: 2,
                borderRadius: 1,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                textAlign: "left",
              }}
            >
              <BusinessIcon
                sx={{ fontSize: 36, color: "primary.main", flexShrink: 0 }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {orgName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("accept.invitedTo")}
                </Typography>
              </Box>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label={t("common.email")}
                value={email}
                disabled
              />
              <TextField
                fullWidth
                required
                margin="normal"
                label={t("accept.yourName")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={phase === "submitting"}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                label={t("accept.createPassword")}
                type={passwordVisible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={phase === "submitting"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t("accept.togglePasswordVisibility")}
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        edge="end"
                      >
                        {passwordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                required
                margin="normal"
                label={t("accept.confirmPassword")}
                type={passwordVisible ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={phase === "submitting"}
              />

              {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {submitError}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={phase === "submitting"}
                sx={{ mt: 3, mb: 2 }}
              >
                {phase === "submitting" ? (
                  <CircularProgress size={24} />
                ) : (
                  t("accept.acceptButton")
                )}
              </Button>

              <Typography variant="caption" color="text.secondary">
                {t("accept.formCaption")}
              </Typography>
            </Box>
          </Box>
        )}

        {phase === "success" && (
          <>
            <CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} />
            <Typography variant="h5" component="h1" sx={{ mt: 2 }}>
              {t("accept.successTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("accept.successBody", { orgName })}
            </Typography>
            <CircularProgress size={24} sx={{ mt: 3 }} />
          </>
        )}
      </Paper>
    </Container>
  );
}

export default AcceptInvite;
